import React, { FC, useRef, useState } from "react";
import { createUseStyles, useTheme } from "react-jss";

import InstagramObject from "Components/InstagramObject";
import MediaIcon from "Components/MediaIcon";
import { AudioPlayer } from "Containers/AudioPlayer";
import { pauseAudio } from "Containers/AudioPlayer/AudioPlayerActions";
import clsx from "clsx";
import { sanitize } from "dompurify";

import Close from "Assets/images/close.svg";

import Pano from "Assets/images/pano_icon.svg";
import Video from "Assets/images/video_icon.svg";
import Photo from "Assets/images/photo_icon.svg";
import CampusReel from "Assets/images/campusreels.svg";
import Instagram from "Assets/images/instagram.svg";
import AudioPlayLarge from "Assets/images/audio_play_large.svg";
import AudioPauseLarge from "Assets/images/audio_pause_large.svg";
import { useDispatch } from "react-redux";
import { setStopState, StopState } from "Containers/Stops";
import {
  setSelectedIndex,
  setShowSmallViewPort,
  setShowMediaObj,
} from "Containers/MediaViewer";
import { motion } from "framer-motion";
import { getOneTrustSafeImgSrc, isEnterOrSpace } from "Utils/utils";
import styles from "./styles";

interface Props {
  media: Array<any>;
  audio: any;
  setShowMediaViewer: (val: any) => void;
  stop: any;
  resetAudio: () => void;
  children: any;
  handleMediaClick: any;
  mediaObj: any;
  errorIdx: any;
  setErrorIdx: (idx: any) => void;
  setSceneOpen: () => void;
  smallViewPort: boolean;
}

function getIcon(mediaType: String, network: string, type: string) {
  if (network === "instagram") return Instagram;
  if (type === "campusreel") return CampusReel;
  switch (mediaType) {
    case "panorama":
      return Pano;
    case "video":
      return Video;
    case "photo":
      return Photo;
    default:
      return "";
  }
}

const MediaViewerSmallViewPort: FC<Props> = (props: Props) => {
  const {
    media,
    audio,
    stop,
    setShowMediaViewer,
    resetAudio,
    children,
    handleMediaClick,
    mediaObj,
    errorIdx,
    setErrorIdx,
    setSceneOpen,
    smallViewPort,
  } = props;

  const [isOpen, setIsOpen] = useState(true);
  const dispatch = useDispatch();
  const useStyles = createUseStyles(styles);
  const theme = useTheme();
  const classes = useStyles({ theme, ...props });
  const sceneAnimations = {
    open: {
      x: "0%",
      transition: {
        ease: [0.28, 0.57, 0.2, 1],
        duration: 0.5,
      },
    },
    closed: {
      x: "-100%",
      transition: {
        ease: "easeIn",
        duration: 0.25,
        onComplete: () => {
          dispatch(setShowMediaViewer(false));
          resetAudio();
        },
      },
    },
  };

  const scrollContainer = useRef(null);

  const [title, setTitle] = useState("");

  if (media.length === 0) return null;
  const onScroll = () => {
    const elem = scrollContainer.current;
    if (elem.scrollTop !== 0) setTitle(stop.title);
    else setTitle("");
  };

  const handleClose = () => {
    resetAudio();
    setIsOpen(false);
    dispatch(setStopState([stop.id], StopState.Visited));
    dispatch(setSelectedIndex(0));
    dispatch(setShowSmallViewPort(false));
    dispatch(setShowMediaObj(false));
    setSceneOpen();
  };

  function pauseStopAudio(event: any) {
    /* eslint-disable no-unused-expressions */
    event?.stopPropagation();
    audio && audio?.pause();
    dispatch(pauseAudio());
  }

  return (
    <motion.div
      initial={{ x: "-100%" }}
      animate={isOpen || smallViewPort ? "open" : "closed"}
      variants={sceneAnimations}
      className={classes.container}
      data-testid="container"
    >
      <div
        role="button"
        tabIndex={0}
        className={classes.closeContainer}
        onClick={(event) => {
          event.stopPropagation();
          handleClose();
        }}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (isEnterOrSpace(event)) {
            handleClose();
          }
        }}
        data-testid="close-container"
      >
        <span className={classes.closeContainerTitle}>{title}</span>

        <span style={{ float: "right" }}>
          <Close />
        </span>
      </div>
      <div
        className={classes.scrollContainer}
        ref={scrollContainer}
        onScroll={onScroll}
      >
        <div className={classes.mainImageContainer}>
          {media[0].network === "instagram" ? (
            <InstagramObject
              embedId={
                media[0].mediaType === "photo" ? media[0].url : media[0].videoid
              }
              alt={media[0].title}
              className={classes.image}
              top={-54}
              height="100%"
              width="100%"
              iframeHeight="154%"
              iframeWidth="100%"
            />
          ) : media[0].type === "campusreel" ? (
            <iframe
              src={media[0].url}
              title={media[0].title}
              tabIndex={-1}
              width="160"
              height="98"
              className={clsx(classes.image, classes.iframe)}
              allowFullScreen
            />
          ) : (
            <img
              src={getOneTrustSafeImgSrc(media[0].mediumurl)}
              alt={media[0].title}
              className={classes.image}
            />
          )}
        </div>

        <div className={classes.scene}>
          <div className={classes.sceneTop}>
            <span className={classes.title}>{stop.title}</span>
            {stop.audio && (
              <span style={{ alignSelf: "flex-start" }}>
                <AudioPlayer
                  url={stop.audio}
                  icon={AudioPlayLarge}
                  pauseIcon={AudioPauseLarge}
                />
              </span>
            )}
          </div>
          {stop.fullMapDescription && (
            /* eslint-disable react/no-danger */
            <div
              className={classes.description}
              dangerouslySetInnerHTML={{
                __html: sanitize(stop.fullMapDescription),
              }}
            />
          )}
        </div>
        <div className={classes.mediaContainer}>
          {media.map((obj: any, idx: number) => {
            const iconImage = getIcon(obj.mediaType, obj.network, obj.type);
            return (
              <div
                role="button"
                tabIndex={0}
                className={classes.imgContainer}
                key={obj.id}
                data-testid="img-media-container"
                onClick={(event: any) => {
                  pauseStopAudio(event);
                  handleMediaClick(idx);
                }}
                onKeyDown={(event) => {
                  pauseStopAudio(event);
                  if (isEnterOrSpace(event)) {
                    handleMediaClick(idx);
                  }
                }}
              >
                {obj.network === "instagram" ? (
                  <InstagramObject
                    className={classes.image}
                    embedId={obj.mediaType === "photo" ? obj.url : obj.videoid}
                    alt={obj.title}
                    onClick={(event: any) => {
                      event.stopPropagation();
                      handleMediaClick(idx);
                    }}
                    height={127}
                    width="100%"
                    top={-54}
                    left={obj.mediaType === "photo" ? 0 : -90}
                    iframeHeight="154%"
                    iframeWidth={obj.mediaType === "photo" ? "100%" : "350px"}
                  />
                ) : obj.type === "campusreel" ? (
                  <iframe
                    src={obj.url}
                    title={obj.title}
                    tabIndex={-1}
                    width="160"
                    height="98"
                    className={clsx(classes.image, classes.iframe)}
                    allowFullScreen
                  />
                ) : (
                  <img
                    src={getOneTrustSafeImgSrc(obj.smallurl)}
                    alt={obj.title}
                    className={classes.image}
                    onError={(e) => {
                      const parentEl = e.currentTarget
                        .parentNode as HTMLElement | null;
                      if (parentEl) parentEl.style.display = "none";
                      setErrorIdx([...errorIdx, obj.id]);
                    }}
                  />
                )}
                <div className={classes.thumbnailIcon}>
                  <MediaIcon
                    Icon={iconImage}
                    className={
                      obj.type === "campusreel" && classes.campusReelIcon
                    }
                    showBackground={false}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {mediaObj && children}
    </motion.div>
  );
};

export default MediaViewerSmallViewPort;
